import {
  DownloadOutlined,
  EyeOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Alert, Badge, Button, Spin, Typography } from 'antd';
import React, { CSSProperties, useMemo, useRef } from 'react';
import { VariableSizeList as List } from 'react-window';
import { useSearchAction } from '../../hooks/use-search-action';
import {
  ListItemType,
  SearchDetailItem,
  SearchResultDataItem,
  SearchResultItem,
  SearchResultProps,
} from '../../interfaces/global-search';
import {
  DetailType,
  SearchCategory,
} from '../../utilities/global-search-enums';
import {
  capitalizeText,
  convertDate,
  DOKUMENT,
  EKSPERT_TRAJNIMI,
  LISTE_PREZENCA,
  TRAJNIM_PREZENCA,
  TRAJNIMI,
} from '../common/common-functions/common-functions';
import { getCategoryMeta } from './data';
import './styles.css';
import { Module } from '../../utilities/enums';

const { Text } = Typography;

interface ListItem {
  index: number;
  style: CSSProperties;
}

const SearchResults = ({
  onClose,
  selectedIndex,
  searchQuery = '',
  searchData,
  activeCategory,
  needsMoreCharacters,
  isLoading,
  error,
  numberOfCharacters = 3,
  selectedModule,
}: SearchResultProps) => {
  const listRef = useRef<List>(null);

  const {
    handleFileDownload,
    handleResultClick,
    isLoading: isDownloading,
    downloadingId,
  } = useSearchAction({
    onClose,
    selectedModule,
  });

  const filteredResults = useMemo(() => {
    if (searchQuery.trim().length < numberOfCharacters) {
      return [];
    }

    const results: SearchResultItem[] = [];

    Object.entries(searchData).forEach(([categoryKey, categoryData]) => {
      if (activeCategory !== 'All' && categoryKey !== activeCategory) {
        return;
      }

      if (Array.isArray(categoryData)) {
        categoryData.forEach((item: SearchResultDataItem, index: number) => {
          results.push({
            id: `${categoryKey}-${item.entityReference?.downloadId || index}`,
            category: categoryKey as SearchCategory,
            title: item.description,
            subtitle: getCategoryMeta(categoryKey).label,
            description: '',
            details: item.details || [],
            icon: categoryKey,
            entityReference: item.entityReference,
          });
        });
      }
    });

    return results;
  }, [searchData, searchQuery, activeCategory, numberOfCharacters]);

  const groupedResults = useMemo(() => {
    const grouped: Record<string, SearchResultItem[]> = {};

    filteredResults.forEach(result => {
      if (!grouped[result.category]) {
        grouped[result.category] = [];
      }
      grouped[result.category].push(result);
    });

    return grouped;
  }, [filteredResults]);

  const sortedCategories = useMemo(() => {
    return Object.entries(groupedResults).sort((a, b) => {
      if (a[0].includes('DOKUMENT')) return 1;
      if (b[0].includes('DOKUMENT')) return -1;
      return 0;
    });
  }, [groupedResults]);

  const listItems = useMemo(() => {
    const items: ListItemType[] = [];
    let globalIndex = 0;

    sortedCategories.forEach(([category, results]) => {
      items.push({
        type: 'header',
        category: capitalizeText(category, true),
        count: results.length,
      });

      results.forEach(result => {
        items.push({
          type: 'result',
          result,
          globalIndex,
        });
        globalIndex++;
      });
    });

    return items;
  }, [sortedCategories]);

  const shouldVirtualize = filteredResults.length > 50;

  const formatDetailValue = (detail: SearchDetailItem): string => {
    if (detail.detailType === 'DATE' && detail.value) {
      return convertDate(detail.value, 'dd/MM/yyyy');
    }

    if (detail.detailType === DetailType.ENUM && detail.value) {
      return capitalizeText(detail.value, true);
    }
    return detail.value || '';
  };

  const getItemSize = (index: number): number => {
    const item = listItems[index];
    if (item.type === 'header') {
      return 56;
    }

    const result = item.result;
    const detailsCount = result.details.filter(d =>
      formatDetailValue(d),
    ).length;
    const detailRows = Math.ceil(detailsCount / 3);
    return 80 + detailRows * 24;
  };

  const buttonLabel = (result: SearchResultItem) => {
    const isTraining = [
      TRAJNIMI,
      EKSPERT_TRAJNIMI,
      TRAJNIM_PREZENCA,
      DOKUMENT,
    ].includes(result?.entityReference?.searchSubModule);

    if (selectedModule === Module.KONKURRIME) {
      return 'Shiko Konkurrent';
    }

    if (selectedModule === Module.FF) {
      if (result?.category === DOKUMENT) {
        return 'Shiko Material';
      }

      if (result?.category === LISTE_PREZENCA) {
        return 'Shiko Listëprezenca';
      }
    }

    return isTraining ? 'Shiko Trajnim' : 'Shiko';
  };

  const renderDocumentActions = (result: SearchResultItem) => {
    return (
      <div className="flex items-center gap-2">
        <Button
          disabled={!result.entityReference?.redirectId}
          icon={<EyeOutlined />}
          size="small"
          onClick={e => {
            e.stopPropagation();
            if (result.entityReference?.redirectId) {
              handleResultClick(result);
            }
          }}
          className="view-button cursor-pointer"
        >
          {buttonLabel(result)}
        </Button>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          size="small"
          disabled={!result.entityReference?.downloadId}
          onClick={e => {
            e.stopPropagation();
            if (result.entityReference?.downloadId) {
              handleFileDownload(
                result.entityReference.downloadId,
                result.title ?? result.description,
              );
            }
          }}
          loading={
            isDownloading &&
            downloadingId === result.entityReference?.downloadId
          }
          className="download-button cursor-pointer"
        >
          Shkarko
        </Button>
      </div>
    );
  };

  const renderListItem = ({ index, style }: ListItem) => {
    const item = listItems[index];

    if (item.type === 'header') {
      const categoryMeta = getCategoryMeta(item.category);
      return (
        <div style={style} className="search-category-group">
          <div className="category-header">
            <div className="category-header-content">
              <span
                className="category-icon"
                style={{ color: categoryMeta.color }}
              >
                {categoryMeta.icon}
              </span>
              <Text className="category-title">{categoryMeta.label}</Text>
              <Badge
                count={item.count}
                style={{
                  backgroundColor: '#f0f0f0',
                  color: '#595959',
                }}
              />
            </div>
          </div>
        </div>
      );
    }

    const { result, globalIndex } = item;
    const category = getCategoryMeta(result.category);
    const isSelected = globalIndex === selectedIndex;
    const isDokument = result.category === SearchCategory.DOKUMENT;

    return (
      <div style={style}>
        <div
          className={`search-result-item ${isSelected ? 'selected' : ''} ${
            isDokument ? 'with-download' : ''
          }`}
          style={{ cursor: 'pointer' }}
        >
          <div
            className="result-icon"
            style={{ backgroundColor: `${category.color}15` }}
          >
            <span style={{ color: category.color }}>{category.icon}</span>
          </div>
          <div className="result-content">
            <div className="result-title-row">
              <Text className="result-title" title={result.title}>
                {result.title}
              </Text>
            </div>

            {result.details && result.details.length > 0 && (
              <div className="result-details-grid">
                {result.details.map(
                  (detail, idx) =>
                    formatDetailValue(detail) && (
                      <div
                        key={idx}
                        className="result-detail-bullet"
                        title={formatDetailValue(detail)}
                      >
                        <span className="bullet-dot">•</span>
                        <Text className="bullet-text">
                          {formatDetailValue(detail)}
                        </Text>
                      </div>
                    ),
                )}
              </div>
            )}
          </div>

          <div className="result-action">
            {isDokument ? (
              renderDocumentActions(result)
            ) : (
              <Button
                type="default"
                icon={<EyeOutlined />}
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  if (result.entityReference?.redirectId) {
                    handleResultClick(result);
                  }
                }}
                className="view-button cursor-pointer"
              >
                {buttonLabel(result)}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderNonVirtualized = () => {
    return (
      <div className="results-container">
        {sortedCategories.map(([category, results]) => (
          <div key={category} className="search-category-group">
            <div className="category-header">
              <div className="category-header-content">
                <span
                  className="category-icon"
                  style={{ color: getCategoryMeta(category).color }}
                >
                  {getCategoryMeta(category).icon}
                </span>
                <Text className="category-title">
                  {getCategoryMeta(category).label}
                </Text>
                <Badge
                  count={results.length}
                  style={{
                    backgroundColor: '#f0f0f0',
                    color: '#595959',
                  }}
                />
              </div>
            </div>

            <div className="category-results">
              {results.map(result => {
                const category = getCategoryMeta(result.category);
                const isDokument = result.category === SearchCategory.DOKUMENT;

                return (
                  <div
                    key={result.id}
                    className="search-result-item"
                    style={{ cursor: 'pointer' }}
                  >
                    <div
                      className="result-icon"
                      style={{ backgroundColor: `${category.color}15` }}
                    >
                      <span style={{ color: category.color }}>
                        {category.icon}
                      </span>
                    </div>
                    <div className="result-content">
                      <div className="result-title-row">
                        <Text className="result-title" title={result.title}>
                          {result.title}
                        </Text>
                      </div>

                      {result.details && result.details.length > 0 && (
                        <div className="result-details-grid">
                          {result.details.map(
                            (detail, idx) =>
                              formatDetailValue(detail) && (
                                <div
                                  key={idx}
                                  className="result-detail-bullet"
                                  title={formatDetailValue(detail)}
                                >
                                  <span className="bullet-dot">•</span>
                                  <Text className="bullet-text">
                                    {formatDetailValue(detail)}
                                  </Text>
                                </div>
                              ),
                          )}
                        </div>
                      )}
                    </div>

                    <div className="result-action">
                      {isDokument ? (
                        renderDocumentActions(result)
                      ) : (
                        <Button
                          icon={<EyeOutlined />}
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            if (result.entityReference?.redirectId) {
                              handleResultClick(result);
                            }
                          }}
                          className="view-button cursor-pointer"
                        >
                          {buttonLabel(result)}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (needsMoreCharacters) {
    return (
      <div className="no-results">
        <div className="no-results-icon">
          <SearchOutlined />
        </div>
        <Text className="no-results-title">
          Shkruani të paktën {numberOfCharacters} karaktere për të kërkuar
        </Text>
        <Text className="no-results-hint">
          {searchQuery.length}/{numberOfCharacters} karaktere
        </Text>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="no-results">
        <Spin size="large" />
        <Text className="no-results-title" style={{ marginTop: '16px' }}>
          Duke kërkuar...
        </Text>
      </div>
    );
  }

  if (error) {
    return (
      <div className="no-results">
        <Alert
          message="Gabim në kërkim"
          description="Ka ndodhur një gabim gjatë kërkimit. Ju lutem provoni përsëri."
          type="error"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      </div>
    );
  }

  if (
    filteredResults.length === 0 &&
    searchQuery.trim().length >= numberOfCharacters
  ) {
    return (
      <div className="no-results">
        <div className="no-results-icon">
          <SearchOutlined />
        </div>
        <Text className="no-results-title">Nuk u gjet asnjë rezultat</Text>
        <Text className="no-results-hint">
          Provoni të kërkoni me fjalë kyçe të ndryshme
        </Text>
      </div>
    );
  }

  if (searchQuery.trim().length === 0) {
    return (
      <div className="no-results">
        <div className="no-results-icon">
          <SearchOutlined />
        </div>
        <Text className="no-results-title">
          Filloni të shkruani për të kërkuar{' '}
          <span className="text-sm font-normal">
            (Të paktën {numberOfCharacters} karaktere)
          </span>
        </Text>
      </div>
    );
  }

  if (shouldVirtualize) {
    return (
      <List
        ref={listRef}
        height={600}
        itemCount={listItems.length}
        itemSize={getItemSize}
        width="100%"
        className="virtualized-results-list"
      >
        {renderListItem}
      </List>
    );
  }

  return renderNonVirtualized();
};

export default SearchResults;
